import { useCallback } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { CodeIcon } from 'foundations-assets/icons/components/CodeIcon';
import { FileIcon } from 'foundations-assets/icons/components/FileIcon';
import { LessonIcon } from 'foundations-assets/icons/components/LessonIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { ConditionalIcon } from 'foundations-assets/icons/components/ConditionalIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TOOLTIP_DELAY_MS = 500;
const TYPE_DISPLAY = {
  HTML: {
    icon: /*#__PURE__*/_jsx(CodeIcon, {
      size: "200"
    }),
    labelMessage: 'breezeEngineChatComponents.artifact.type.html',
    isI18n: true
  },
  MARKDOWN: {
    icon: /*#__PURE__*/_jsx(LessonIcon, {
      size: "200"
    }),
    labelMessage: 'breezeEngineChatComponents.artifact.type.markdown',
    isI18n: true
  },
  // TODO: temp — EMAIL mirrors MARKDOWN until a dedicated email formatter lands
  // (see TODO in artifact-block/registry.ts).
  EMAIL: {
    icon: /*#__PURE__*/_jsx(LessonIcon, {
      size: "200"
    }),
    labelMessage: 'breezeEngineChatComponents.artifact.type.email',
    isI18n: true
  },
  LANDING_PAGE: {
    icon: /*#__PURE__*/_jsx(LessonIcon, {
      size: "200"
    }),
    labelMessage: 'breezeEngineChatComponents.artifact.type.landingPage',
    isI18n: true
  },
  BLOG_POST: {
    icon: /*#__PURE__*/_jsx(LessonIcon, {
      size: "200"
    }),
    labelMessage: 'breezeEngineChatComponents.artifact.type.blogPost',
    isI18n: true
  },
  SITE_PAGE: {
    icon: /*#__PURE__*/_jsx(LessonIcon, {
      size: "200"
    }),
    labelMessage: 'breezeEngineChatComponents.artifact.type.sitePage',
    isI18n: true
  },
  FORMS_CONDITIONAL_LOGIC: {
    icon: /*#__PURE__*/_jsx(ConditionalIcon, {
      size: "200"
    }),
    labelMessage: 'breezeEngineChatComponents.artifact.type.formsConditionalLogic',
    isI18n: true
  }
};
const getDisplay = (artifactType, getArtifactPlugin) => {
  var _TYPE_DISPLAY$artifac;
  const plugin = getArtifactPlugin === null || getArtifactPlugin === void 0 ? void 0 : getArtifactPlugin(artifactType);
  if (plugin !== null && plugin !== void 0 && plugin.icon) {
    const Icon = plugin.icon;
    return {
      icon: /*#__PURE__*/_jsx(Icon, {
        size: "200"
      }),
      labelMessage: plugin.label,
      isI18n: false
    };
  }
  return (_TYPE_DISPLAY$artifac = TYPE_DISPLAY[artifactType]) !== null && _TYPE_DISPLAY$artifac !== void 0 ? _TYPE_DISPLAY$artifac : {
    icon: /*#__PURE__*/_jsx(FileIcon, {
      size: "200"
    }),
    labelMessage: '',
    isI18n: true
  };
};
export function DefaultArtifactBlock({
  block,
  onArtifactClick,
  disableArtifacts,
  getArtifactPlugin
}) {
  const {
    icon,
    labelMessage,
    isI18n
  } = getDisplay(block.artifactType, getArtifactPlugin);
  const handleClick = useCallback(() => {
    onArtifactClick === null || onArtifactClick === void 0 || onArtifactClick(block);
  }, [onArtifactClick, block]);
  const interactive = !disableArtifacts && !!onArtifactClick;
  return /*#__PURE__*/_jsx(Tooltip, {
    disabled: !disableArtifacts,
    delay: TOOLTIP_DELAY_MS,
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "breezeEngineChatComponents.artifact.disabledTooltip"
    }),
    children: /*#__PURE__*/_jsx("div", Object.assign({
      "data-block-type": "artifact"
    }, disableArtifacts ? {
      'aria-disabled': true
    } : interactive ? {
      role: 'button',
      tabIndex: 0
    } : {}, {
      onClick: interactive ? handleClick : undefined,
      onKeyDown: interactive ? event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleClick();
        }
      } : undefined,
      children: /*#__PURE__*/_jsx(CardWrapper, {
        cursor: interactive ? 'pointer' : undefined,
        padding: "0",
        borderRadius: "300",
        borderWidth: "100",
        borderStyle: "solid",
        borderColor: "border-core-subtle",
        children: /*#__PURE__*/_jsx(CardSection, {
          padding: "0",
          children: /*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            paddingInline: "400",
            paddingBlock: "300",
            gap: "200",
            children: [/*#__PURE__*/_jsx(Flex, {
              backgroundColor: "fill-surface-recessed",
              borderRadius: "full",
              padding: "200",
              flexShrink: 0,
              alignItems: "center",
              justifyContent: "center",
              children: icon
            }), /*#__PURE__*/_jsxs(Flex, {
              flex: "1",
              flexDirection: "column",
              gap: "0",
              minInlineSize: "0",
              children: [/*#__PURE__*/_jsx(Text, {
                typography: "heading-50",
                truncate: true,
                children: block.name
              }), /*#__PURE__*/_jsx(Text, {
                typography: "detail-100",
                children: labelMessage ? isI18n ? /*#__PURE__*/_jsx(FormattedMessage, {
                  message: labelMessage
                }) : labelMessage : block.artifactType
              })]
            }), block.version != null && /*#__PURE__*/_jsx(Text, {
              typography: "detail-100",
              paddingInlineStart: "200",
              children: unescapedText('breezeEngineChatComponents.artifact.version', {
                version: block.version
              })
            })]
          })
        })
      })
    }))
  });
}